import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, mediaUrl } from "@/lib/api";

const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-magenta";

export default function GalleryManager() {
  const [items, setItems] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/gallery", { url: r.data.url, caption: caption.trim() });
      }
      setCaption("");
      toast.success(files.length > 1 ? `${files.length} photos added to the gallery` : "Photo added to the gallery");
      await load();
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Photo removed from the gallery");
    load();
  };

  return (
    <div data-testid="gallery-manager" className="max-w-4xl pb-24">
      <h1 className="font-heading text-2xl font-semibold">Photo Gallery</h1>
      <p className="text-sm text-slate-500">Photos uploaded here appear on the Media &amp; Press page of the website.</p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Caption (optional — applied to the photos you upload now)</label>
        <input className={inp} placeholder="e.g. Annual Wellness Retreat 2026" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-caption-input" />
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="gallery-upload-input" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="gallery-upload-button"
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-magenta px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
          <ImagePlus className="h-4 w-4" /> {busy ? "Uploading…" : "Upload Photos"}
        </button>
      </div>

      {!items ? (
        <p className="mt-8 text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-8 rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="gallery-admin-empty">
          No photos yet. Upload your first one above.
        </p>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-admin-grid">
          {items.map((g, i) => (
            <div key={g.id} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`gallery-admin-item-${i}`}>
              <img src={mediaUrl(g.url)} alt={g.caption || "Gallery photo"} className="h-36 w-full object-cover" />
              {g.caption && <p className="truncate px-2.5 py-2 text-xs text-slate-600">{g.caption}</p>}
              <button type="button" onClick={() => remove(g.id)} aria-label="Remove photo" data-testid={`gallery-delete-${i}`}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-500 opacity-0 shadow transition-opacity hover:text-red-500 group-hover:opacity-100">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
